import type { FormAnswers, FormDefinition } from '@serviceportal/shared';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Loader2, MailCheck } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { FormRenderer } from '@/components/form-renderer';
import { Alert, Card } from '@/components/ui/primitives';
import { api, ApiError, errorMessage } from '@/lib/api';
import { applyBranding, logoUrl } from '@/lib/branding';
import { solveChallenge } from '@/lib/pow';
import { cn } from '@/lib/utils';

interface PublicForm {
  name: string;
  description: string | null;
  definition: FormDefinition;
  requireEmailConfirmation: boolean;
  tenant: { name: string; primaryColor: string; hasLogo: boolean; brandingVersion: string };
}

/** Meldet der einbettenden Seite die Höhe, damit das iframe ohne Scrollbalken wächst. */
function useEmbedHeight(enabled: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!enabled || !ref.current || window.parent === window) return;
    const ro = new ResizeObserver(() => {
      window.parent.postMessage({ type: 'serviceportal:height', height: Math.ceil(document.documentElement.scrollHeight) }, '*');
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [enabled]);
  return ref;
}

function Shell({ embed, form, children }: { embed: boolean; form?: PublicForm; children: ReactNode }) {
  const ref = useEmbedHeight(embed);
  return (
    <div ref={ref} className={cn(embed ? 'bg-transparent p-1' : 'min-h-dvh bg-gradient-to-b from-primary/8 via-background to-background px-4 py-8 sm:py-12')}>
      <div className={cn('mx-auto w-full', embed ? 'max-w-none' : 'max-w-2xl')}>
        {!embed && form && (
          <div className="mb-6 flex items-center gap-3">
            {form.tenant.hasLogo ? (
              <img src={logoUrl(form.tenant.brandingVersion)} alt={form.tenant.name} className="h-10 max-w-[180px] object-contain" />
            ) : (
              <span className="flex size-10 items-center justify-center rounded-lg bg-primary font-bold text-primary-foreground">{form.tenant.name.charAt(0)}</span>
            )}
            <span className="text-sm font-medium text-muted-foreground">{form.tenant.name}</span>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

export function PublicFormPage() {
  const { slug } = useParams<{ slug: string }>();
  const [params] = useSearchParams();
  const embed = params.get('embed') === '1';
  const [result, setResult] = useState<{ status: 'PENDING_CONFIRMATION' | 'PROCESSED'; ticketNumber: string | null } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const pow = useRef<Promise<{ challenge: string; nonce: string }> | null>(null);

  const { data: form, error: loadError, isLoading } = useQuery({
    queryKey: ['public-form', slug],
    queryFn: () => api<PublicForm>(`/public/forms/${slug}`),
    retry: false,
  });

  /** Neue Sicherheitsaufgabe holen und im Hintergrund lösen. */
  const prepare = () => {
    pow.current = api<{ challenge: string; difficulty: number }>(`/public/forms/${slug}/challenge`).then(async (c) => ({
      challenge: c.challenge,
      nonce: await solveChallenge(c.challenge, c.difficulty),
    }));
    pow.current.catch(() => undefined);
  };

  useEffect(() => {
    if (!form) return;
    applyBranding({ name: form.name, primaryColor: form.tenant.primaryColor, brandingVersion: form.tenant.brandingVersion });
    prepare();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const submit = async (data: FormAnswers) => {
    setSubmitting(true);
    setError(null);
    setServerErrors({});
    try {
      const solved = await pow.current!;
      const r = await api<{ status: 'PENDING_CONFIRMATION' | 'PROCESSED'; ticketNumber: string | null }>(`/public/forms/${slug}/submit`, {
        method: 'POST',
        body: { data, ...solved, embedded: embed },
      });
      setResult(r);
    } catch (e) {
      if (e instanceof ApiError && e.details) setServerErrors(Object.fromEntries(e.details.map((d) => [d.path, d.message])));
      setError(errorMessage(e));
      prepare();
    } finally {
      setSubmitting(false);
    }
  };

  const upload = async (file: File) => {
    const fd = new window.FormData();
    fd.append('file', file);
    return api<{ token: string; name: string }>(`/public/forms/${slug}/files`, { method: 'POST', form: fd });
  };

  if (isLoading) {
    return (
      <Shell embed={embed}>
        <div className="flex justify-center py-20">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      </Shell>
    );
  }
  if (loadError || !form) {
    return (
      <Shell embed={embed}>
        <Alert>Dieses Formular ist nicht (mehr) verfügbar.</Alert>
      </Shell>
    );
  }

  return (
    <Shell embed={embed} form={form}>
      <Card className={cn('p-5 sm:p-8', embed && 'shadow-none')}>
        {result ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            {result.status === 'PENDING_CONFIRMATION' ? <MailCheck className="size-12 text-primary" /> : <CheckCircle2 className="size-12 text-success" />}
            <h1 className="text-xl font-semibold">{result.status === 'PENDING_CONFIRMATION' ? 'Fast geschafft!' : form.definition.confirmation.title}</h1>
            <p className="max-w-md whitespace-pre-wrap text-sm text-muted-foreground">
              {result.status === 'PENDING_CONFIRMATION'
                ? 'Wir haben Ihnen eine E-Mail geschickt. Bitte bestätigen Sie Ihre Anfrage über den Link darin – erst dann wird sie bearbeitet.'
                : form.definition.confirmation.message}
            </p>
            {result.ticketNumber && <p className="text-sm">Ihre Vorgangsnummer: <strong className="font-mono">{result.ticketNumber}</strong></p>}
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-semibold tracking-tight">{form.name}</h1>
            {form.description && <p className="mt-1 text-sm text-muted-foreground">{form.description}</p>}
            <FormRenderer className="mt-6" definition={form.definition} onSubmit={submit} uploadFile={upload} submitting={submitting} serverErrors={serverErrors} error={error} />
            <p className="mt-6 text-xs text-muted-foreground">
              Ihre Angaben werden ausschließlich zur Bearbeitung Ihrer Anfrage durch {form.tenant.name} verarbeitet.
            </p>
          </>
        )}
      </Card>
    </Shell>
  );
}

export function ConfirmSubmissionPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [state, setState] = useState<{ ok: true; number: string; form: string } | { ok: false; message: string } | null>(null);
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    api<{ ticketNumber: string; formName: string }>('/public/forms/confirm', { method: 'POST', body: { token } })
      .then((r) => setState({ ok: true, number: r.ticketNumber, form: r.formName }))
      .catch((e) => setState({ ok: false, message: errorMessage(e) }));
  }, [token]);
  return (
    <Shell embed={false}>
      <Card className="p-8 text-center">
        {!state && <Loader2 className="mx-auto size-6 animate-spin text-muted-foreground" />}
        {state?.ok && (
          <div className="flex flex-col items-center gap-3">
            <CheckCircle2 className="size-12 text-success" />
            <h1 className="text-xl font-semibold">Vielen Dank – Ihre Anfrage ist bestätigt</h1>
            <p className="text-sm text-muted-foreground">
              Wir bearbeiten Ihre Anfrage unter der Nummer <strong className="font-mono">{state.number}</strong>. Eine Bestätigung haben Sie per E-Mail erhalten.
            </p>
          </div>
        )}
        {state && !state.ok && <Alert variant="destructive">{state.message}</Alert>}
      </Card>
    </Shell>
  );
}
